/**
 * Erro de chamada HTTP à API. Carrega `status` para a triagem 4xx vs 5xx
 * feita no handler global do Sentry (ver App.tsx).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  readonly method: string;
  readonly body?: string;

  constructor(params: {
    status: number;
    url: string;
    method: string;
    body?: string;
  }) {
    super(`HTTP ${params.status} ${params.method} ${params.url}`);
    this.name = "ApiError";
    this.status = params.status;
    this.url = params.url;
    this.method = params.method;
    this.body = params.body;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

export function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError && /failed to fetch|network/i.test(err.message);
}

export interface FormattedApiError {
  /** Mensagem curta amigável (ex.: "Erro 500 do servidor"). */
  title: string;
  /** Detalhe útil: `detail` do FastAPI quando houver, senão a mensagem do erro. */
  detail: string;
  /** Status HTTP quando aplicável. */
  status?: number;
  /** Corpo bruto da resposta (pra um <details> opcional). */
  rawBody?: string;
}

/**
 * Extrai uma representação amigável e ÚTIL de um erro vindo de `apiFetch`.
 * Resolve o problema de "0 resultados" encobrindo o motivo real: o `detail`
 * do servidor (padrão FastAPI) fica em `ApiError.body` como JSON e nunca
 * aparecia na UI. Esta função tira ele de lá.
 */
export function formatApiError(err: unknown): FormattedApiError {
  if (isApiError(err)) {
    let detail = err.message;
    if (err.body) {
      try {
        const parsed = JSON.parse(err.body) as { detail?: unknown };
        if (typeof parsed.detail === "string") {
          detail = parsed.detail;
        } else if (Array.isArray(parsed.detail)) {
          detail = describeValidationDetail(parsed.detail);
        } else if (parsed.detail !== undefined) {
          detail = JSON.stringify(parsed.detail);
        }
      } catch {
        // body não é JSON — usa o texto cru se for curto e legível
        if (err.body.length < 300) detail = err.body;
      }
    }
    return {
      title:
        err.status >= 500
          ? `Erro ${err.status} do servidor`
          : err.status === 401 || err.status === 403
            ? "Sem permissão para esta ação"
            : err.status === 404
              ? "Não encontrado"
              : `Erro ${err.status}`,
      detail,
      status: err.status,
      rawBody: err.body,
    };
  }
  if (isNetworkError(err)) {
    return {
      title: "Sem conexão",
      detail: "Não foi possível conectar ao servidor. Verifique sua internet.",
    };
  }
  const userError = toUserError(err);
  return { title: userError.title, detail: userError.description };
}

/** Mensagem legível de qualquer erro (API, Supabase/PostgREST ou Error comum), sem texto cru do banco. */
export function errorMessage(err: unknown): string {
  return toUserError(err).description;
}

/** Erro pronto para mostrar ao usuário (toast, banner, linha de formulário). */
export interface UserError {
  title: string;
  description: string;
  /**
   * `true` quando o erro é uma situação esperada que o usuário entende e resolve
   * (campo obrigatório, registro duplicado, sem permissão). `false` quando é
   * falha nossa ou desconhecida e precisa ir ao Sentry.
   */
  expected: boolean;
  /**
   * Presente quando um erro esperado também deve ir ao Sentry (ver
   * `REPORT_MISSING_REQUIRED_FIELD`): tags para filtrar o evento.
   */
  reportTags?: Record<string, string>;
}

export interface UserErrorContext {
  /** O que o usuário tentava fazer, no infinitivo: "aprovar a vaga" vira "Não foi possível aprovar a vaga". */
  action?: string;
}

/**
 * Campo obrigatório vazio (23502) continua com texto amigável, mas vai ao Sentry
 * com `kind=campo_obrigatorio` e a coluna, para achar formulários sem validação.
 * Para parar de reportar, troque para `false`.
 */
export const REPORT_MISSING_REQUIRED_FIELD = true;

const GENERIC_DESCRIPTION =
  "Aconteceu um erro inesperado. Tente de novo em instantes; se continuar, fale com o administrador.";
const PERMISSION_DESCRIPTION =
  "Você não tem permissão para esta ação. Se precisar dela, fale com o administrador da plataforma.";

/** Rótulos em pt-BR das colunas que a UI grava, usados quando o banco ou a API apontam um campo. */
export const FIELD_LABELS: Record<string, string> = {
  agencia_id: "Agência",
  area_atuacao: "Área de atuação",
  areas: "Áreas",
  cliente: "Cliente",
  comentario: "Comentário",
  complexidade: "Complexidade",
  data_conclusao: "Data de conclusão",
  data_inicio: "Data de início",
  descricao: "Descrição",
  duracao_meses: "Duração em meses",
  email: "E-mail",
  etapa_id: "Etapa",
  freelancer_id: "Freelancer",
  last_status_changed_at: "Data da mudança de status",
  linkedin_url: "LinkedIn",
  localizacao_estado: "Estado",
  localizacao_pais: "País",
  modalidade: "Modalidade",
  motivo: "Motivo",
  motivo_drop: "Motivo do descarte",
  motivo_encerramento: "Motivo do encerramento",
  nome: "Nome",
  nucleo: "Núcleo",
  observacoes: "Observações",
  password: "Senha",
  rating: "Nota",
  responsavel_id: "Responsável",
  status: "Status",
  telefone: "Telefone",
  texto: "Texto",
  tipo: "Tipo",
  titulo: "Título",
  urgencia: "Urgência",
  valor_projeto: "Valor do projeto",
  vaga_id: "Vaga",
  verba_maxima: "Verba máxima",
  verba_minima: "Verba mínima",
  whatsapp: "WhatsApp",
};

/** Mensagens das CHECK constraints das tabelas que a UI grava (nomes em coriscofrilas-api/sql). */
export const CHECK_CONSTRAINT_MESSAGES: Record<string, string> = {
  vagas_last_status_changed_at_not_future:
    "A data da mudança de status ficou no futuro. Confira o relógio do seu computador e tente de novo.",
  vagas_verba_minima_positiva: "A verba mínima precisa ser maior que zero.",
  vagas_verba_maxima_positiva: "A verba máxima precisa ser maior que zero.",
  vagas_verba_maxima_maior_minima: "A verba máxima precisa ser maior ou igual à verba mínima.",
  vagas_duracao_meses_positiva: "A duração em meses precisa ser maior que zero.",
  vagas_localizacao_estado_valido: "O estado precisa ser a sigla com duas letras maiúsculas, como SP.",
  vagas_localizacao_pais_valido: "O país precisa ter pelo menos duas letras.",
  vagas_modalidade_check: "A modalidade precisa ser frila ou fixa.",
  vagas_complexidade_range: "A complexidade precisa ficar entre 1 e 10.",
  vagas_urgencia_range: "A urgência precisa ficar entre 1 e 3.",
  vagas_kanban_historico_drop_requer_motivo: "Informe o motivo do descarte.",
  controle_projetos_data_inicio_required_when_ativo: "Informe a data de início para ativar o projeto.",
  controle_projetos_status_check: "O status do projeto precisa ser rascunho, ativo ou encerrado.",
  controle_projetos_motivo_encerramento_tamanho: "O motivo do encerramento pode ter no máximo 400 caracteres.",
  freelancer_observacoes_autor_manual: "Não foi possível identificar quem escreveu a observação. Entre de novo e tente outra vez.",
  freelancer_agencia_indicacoes_tipo_check: "Só é possível registrar indicações.",
  job_evaluations_origin_check: "A origem da avaliação é inválida.",
  job_evaluations_team_filled_at_check: "Informe a data de preenchimento da avaliação da equipe.",
  omie_clientes_vinculo_unico_tipo: "Vincule o cadastro a uma agência ou a um freelancer, não aos dois.",
};

/** Mensagens das UNIQUE constraints conhecidas; as demais caem no texto genérico de duplicado. */
export const UNIQUE_CONSTRAINT_MESSAGES: Record<string, string> = {
  uq_freelancers_email_lower: "Já existe um freelancer com este e-mail.",
  freelancer_agencia_indicacoes_freelancer_id_agencia_id_key: "Este freelancer já foi indicado por esta agência.",
  uq_eval_group_freelancer_ativo: "Este freelancer já está nesta avaliação.",
  idx_omie_clientes_agencia_unica_por_empresa: "Esta agência já está vinculada a um cadastro desta empresa.",
  idx_omie_clientes_freelancer_unico_por_empresa: "Este freelancer já está vinculado a um cadastro desta empresa.",
};

const SERVER_FAILURE_SQLSTATE = /^(08|53|57|58|XX)[0-9A-Z]{3}$/;
const POSTGREST_NETWORK_MESSAGE = /^(TypeError|FetchError|AbortError)?:?\s*(failed to fetch|network|load failed)/i;
const PROGRAMMING_ERROR_NAMES = new Set(["TypeError", "ReferenceError", "SyntaxError", "RangeError"]);

interface PostgrestLikeError {
  code: string;
  message: string;
  details?: unknown;
  hint?: unknown;
}

function isPostgrestLikeError(error: unknown): error is PostgrestLikeError {
  return (
    !!error &&
    typeof error === "object" &&
    typeof (error as PostgrestLikeError).code === "string" &&
    typeof (error as PostgrestLikeError).message === "string" &&
    ("details" in error || "hint" in error)
  );
}

/** "data_inicio" vira "Data de início"; coluna sem rótulo vira texto legível ("valor_hora" vira "valor hora"). */
export function fieldLabel(column: string): string {
  if (FIELD_LABELS[column]) return FIELD_LABELS[column];
  return column.replace(/_id$/, "").replace(/_/g, " ");
}

function quotedName(message: string, prefix: string): string | null {
  const match = message.match(new RegExp(`${prefix} "([^"]+)"`));
  return match ? match[1] : null;
}

interface ValidationIssue {
  loc?: unknown;
}

/** FastAPI 422: `detail` é uma lista de `{ loc, msg, type }`; vira "Confira os campos: E-mail, Nome." */
export function describeValidationDetail(issues: unknown[]): string {
  const labels = issues
    .map((issue) => {
      const location = (issue as ValidationIssue)?.loc;
      if (!Array.isArray(location)) return null;
      const field = [...location].reverse().find((part) => typeof part === "string" && part !== "body");
      return typeof field === "string" ? fieldLabel(field) : null;
    })
    .filter((label): label is string => !!label);
  const unique = [...new Set(labels)];
  if (unique.length === 0) return "Alguns dados enviados são inválidos. Confira o formulário e tente de novo.";
  if (unique.length === 1) return `Confira o campo ${unique[0]}.`;
  return `Confira os campos: ${unique.join(", ")}.`;
}

function describePostgrestError(error: PostgrestLikeError): Omit<UserError, "title"> {
  const { code, message } = error;
  switch (code) {
    case "23505": {
      const constraint = quotedName(message, "constraint");
      return {
        description:
          (constraint && UNIQUE_CONSTRAINT_MESSAGES[constraint]) ?? "Já existe um registro com esses dados.",
        expected: true,
      };
    }
    case "23502": {
      const column = quotedName(message, "column");
      return {
        description: column ? `Preencha o campo ${fieldLabel(column)}.` : "Preencha todos os campos obrigatórios.",
        expected: true,
        ...(REPORT_MISSING_REQUIRED_FIELD
          ? { reportTags: { kind: "campo_obrigatorio", column: column ?? "desconhecida" } }
          : {}),
      };
    }
    case "23514": {
      const constraint = quotedName(message, "constraint");
      const known = constraint ? CHECK_CONSTRAINT_MESSAGES[constraint] : undefined;
      return {
        description: known ?? "Algum valor informado não é aceito. Confira os campos e tente de novo.",
        expected: !!known,
      };
    }
    case "23503":
      return {
        description: /^update or delete/i.test(message)
          ? "Este registro está ligado a outros e não pode ser removido."
          : "O registro escolhido não existe mais. Atualize a página e tente de novo.",
        expected: true,
      };
    case "22001":
      return { description: "Um dos textos passou do tamanho permitido. Encurte e tente de novo.", expected: true };
    case "42501":
    case "PGRST301":
    case "PGRST303":
      return { description: PERMISSION_DESCRIPTION, expected: true };
    case "PGRST116":
      return { description: "O registro não foi encontrado. Ele pode ter sido removido.", expected: true };
    case "57014":
      return { description: "A operação demorou demais e foi interrompida. Tente de novo em instantes.", expected: false };
  }
  if (SERVER_FAILURE_SQLSTATE.test(code) || /^PGRST00[0-3]$/.test(code)) {
    return { description: "O servidor está com instabilidade. Tente de novo em instantes.", expected: false };
  }
  return { description: GENERIC_DESCRIPTION, expected: false };
}

function describeApiError(error: ApiError): Omit<UserError, "title"> {
  if (error.status === 401) {
    return { description: "Sua sessão expirou. Entre de novo para continuar.", expected: true };
  }
  if (error.status === 403) return { description: PERMISSION_DESCRIPTION, expected: true };
  if (error.status >= 500) {
    return { description: "O servidor está com instabilidade. Tente de novo em instantes.", expected: false };
  }
  let detail: unknown;
  try {
    detail = error.body ? (JSON.parse(error.body) as { detail?: unknown }).detail : undefined;
  } catch {
    detail = undefined;
  }
  if (Array.isArray(detail)) return { description: describeValidationDetail(detail), expected: true };
  if (typeof detail === "string" && detail.trim()) return { description: detail, expected: true };
  if (error.status === 404) return { description: "O registro não foi encontrado. Ele pode ter sido removido.", expected: true };
  if (error.status === 409) return { description: "Já existe um registro com esses dados.", expected: true };
  return { description: "Não foi possível concluir a ação. Confira os dados e tente de novo.", expected: true };
}

/** Falha de rede no `apiFetch` (TypeError) ou dentro do cliente PostgREST (erro sem código). */
function isConnectionError(error: unknown): boolean {
  if (isNetworkError(error)) return true;
  return isPostgrestLikeError(error) && error.code === "" && POSTGREST_NETWORK_MESSAGE.test(error.message);
}

const AUTH_MESSAGES: [RegExp, string][] = [
  [/invalid login credentials/i, "E-mail ou senha incorretos."],
  [/email not confirmed/i, "Confirme seu e-mail antes de entrar."],
  [/user already registered/i, "Já existe uma conta com este e-mail."],
  [/rate limit|too many requests/i, "Muitas tentativas seguidas. Aguarde um pouco e tente de novo."],
  [/password/i, "A senha não atende aos requisitos. Use uma senha mais forte."],
];

/** Erros do Supabase Auth (AuthApiError, AuthWeakPasswordError...) chegam em inglês. */
function describeAuthError(error: Error): Omit<UserError, "title"> {
  const known = AUTH_MESSAGES.find(([pattern]) => pattern.test(error.message));
  return {
    description: known ? known[1] : "Não foi possível autenticar. Confira os dados e tente de novo.",
    expected: !!known,
  };
}

function describeError(error: unknown): Omit<UserError, "title"> {
  if (isConnectionError(error)) {
    return { description: "Não foi possível conectar ao servidor. Verifique sua internet e tente de novo.", expected: false };
  }
  if (isApiError(error)) return describeApiError(error);
  if (isPostgrestLikeError(error)) return describePostgrestError(error);
  if (error instanceof Error && /^Auth[A-Za-z]*Error$/.test(error.name)) return describeAuthError(error);
  if (error instanceof Error && error.name === "SilentWriteError") {
    return {
      description:
        "Nada foi salvo. Talvez você não tenha permissão ou o registro não exista mais. Atualize a página e tente de novo.",
      expected: false,
    };
  }
  if (error instanceof Error && !PROGRAMMING_ERROR_NAMES.has(error.name) && error.message.trim()) {
    // Error comum lançado pelo app já carrega texto em pt-BR para o usuário.
    // eslint-disable-next-line no-restricted-syntax
    return { description: error.message, expected: false };
  }
  return { description: GENERIC_DESCRIPTION, expected: false };
}

/**
 * Normaliza qualquer erro (ApiError do `apiFetch`, erro do PostgREST/Supabase,
 * Error comum ou valor solto) num título e numa descrição em pt-BR para o usuário.
 * Nunca devolve SQL cru nem "[object Object]". Função pura: quem mostra o erro
 * num catch manual usa `userErrorToast` (report-error.ts), que também reporta ao Sentry.
 */
export function toUserError(error: unknown, context: UserErrorContext = {}): UserError {
  const described = describeError(error);
  const title = context.action
    ? `Não foi possível ${context.action}`
    : isConnectionError(error)
      ? "Sem conexão"
      : "Não foi possível concluir";
  return { title, ...described };
}
